import { useState, useCallback } from 'react';
import { recurrenceApi } from '../services/recurrenceApi';
import type {
  RecurrenceSeries,
  RecurrenceSeriesResponse,
  CreateRecurrenceSeriesRequest,
  UpdateRecurrenceSeriesRequest,
  UpdateSingleOccurrenceRequest,
  CancelOccurrenceRequest,
  ConflictValidationResponse,
} from '../types/recurrence';

export const useRecurrence = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createSeries = useCallback(
    async (data: CreateRecurrenceSeriesRequest): Promise<RecurrenceSeries | null> => {
      setLoading(true);
      setError(null);
      try {
        const series = await recurrenceApi.createSeries(data);
        return series;
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || 'Erro ao criar série recorrente';
        setError(errorMessage);
        
        // Se houver conflitos, lançar com detalhes
        if (err.response?.status === 409) {
          throw {
            conflicts: err.response.data.conflicts,
            message: errorMessage
          };
        }
        
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getSeries = useCallback(
    async (seriesId: string): Promise<RecurrenceSeriesResponse | null> => {
      setLoading(true);
      setError(null);
      try {
        const series = await recurrenceApi.getSeries(seriesId);
        return series;
      } catch (err: any) {
        setError(err.response?.data?.error || 'Erro ao buscar série');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateSeries = useCallback(
    async (
      seriesId: string,
      data: UpdateRecurrenceSeriesRequest
    ): Promise<RecurrenceSeries | null> => {
      setLoading(true);
      setError(null);
      try {
        const series = await recurrenceApi.updateSeries(seriesId, data);
        return series;
      } catch (err: any) {
        setError(err.response?.data?.error || 'Erro ao atualizar série');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateOccurrence = useCallback(
    async (seriesId: string, data: UpdateSingleOccurrenceRequest): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await recurrenceApi.updateOccurrence(seriesId, data);
        return true;
      } catch (err: any) {
        setError(err.response?.data?.error || 'Erro ao atualizar ocorrência');
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const cancelOccurrence = useCallback(
    async (seriesId: string, data: CancelOccurrenceRequest): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await recurrenceApi.cancelOccurrence(seriesId, data);
        return true;
      } catch (err: any) {
        setError(err.response?.data?.error || 'Erro ao cancelar ocorrência');
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const validateConflicts = useCallback(
    async (data: CreateRecurrenceSeriesRequest): Promise<ConflictValidationResponse | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await recurrenceApi.validateConflicts(data);
        return result;
      } catch (err: any) {
        setError(err.response?.data?.error || 'Erro ao validar conflitos');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getOccurrences = useCallback(
    async (seriesId: string, startDate: string, endDate: string): Promise<any[]> => {
      setLoading(true);
      setError(null);
      try {
        const occurrences = await recurrenceApi.getOccurrences(seriesId, startDate, endDate);
        return occurrences;
      } catch (err: any) {
        setError(err.response?.data?.error || 'Erro ao buscar ocorrências');
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    loading,
    error,
    createSeries,
    getSeries,
    updateSeries,
    updateOccurrence,
    cancelOccurrence,
    validateConflicts,
    getOccurrences
  };
};

